import { Box, Typography, Button, IconButton, LinearProgress, Rating } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'

export const StyledPageWrapper = styled(Box)({})

export const StyledLoadingWrapper = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
})

export const StyledBannerContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(6),
    padding: theme.spacing(6),
    width: '100%',

    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: theme.spacing(2),
    },
}))

export const StyledPoster = styled(Box)(({ theme }) => ({
    width: 240,
    height: 360,
    borderRadius: Number(theme.shape.borderRadius) * 2,
    overflow: 'hidden',
    flexShrink: 0,
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,

    [theme.breakpoints.down('md')]: {
        width: 200,
        height: 300,
    },
}))

export const StyledPosterImage = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
})

export const StyledPosterFallback = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 64,
    backgroundColor: theme.palette.background.default,
}))

export const StyledBannerInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,

    [theme.breakpoints.down('md')]: {
        alignItems: 'center',
        textAlign: 'center',
        gap: 8
    },
}))

export const StyledTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: 42,
    lineHeight: 1.1,
    [theme.breakpoints.down('md')]: {
        fontSize: 32,
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: 20,
    },
}))

export const StyledTagline = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 16,
    fontStyle: 'italic',
}))

export const StyledMetaWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
}))

interface StyledRatingProps {
    value: number;
}

export const StyledRating = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "value",
})<StyledRatingProps>(({ theme, value }) => {
    let color = theme.palette.green.main;

    if (value < 4) {
        color = theme.palette.error.main;
    } else if (value < 7) {
        color = theme.palette.warning.main;
    }

    return {
        color,
        fontSize: 16,
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 4,
    };
})

export const StyledMetaText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 16,
}))

// Small italic hint next to meta info ("Releases ...", "Mark it as watched to rate")
export const StyledMetaHint = styled(StyledMetaText)({
    fontStyle: 'italic',
    fontSize: 13,
})

export const StyledCertification = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
    color: theme.palette.text.primary,
    padding: '4px 10px',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 500,
}))

export const StyledGenreWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',

    [theme.breakpoints.down('md')]: {
        justifyContent: 'center',
    },
}))

export const StyledGenreChip = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    padding: '6px 14px',
    borderRadius: 20,
    fontSize: 13,
}))

export const StyledButtonWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginTop: theme.spacing(1),

    [theme.breakpoints.down('md')]: {
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: theme.spacing(1),
    },
}))

export const StyledPrimaryButton = styled(Button)(({ theme }) => ({
    color: theme.palette.text.primary,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: 500,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
        borderColor: theme.palette.primary.main,
    },

    [theme.breakpoints.down('md')]: {
        padding: '10px 24px',
        fontSize: 14,
    },
}))

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    padding: 12,
    '&:hover': {
        borderColor: theme.palette.text.secondary,
    },
}))

export const StyledContentWrapper = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: theme.spacing(6),
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        textAlign: 'center',
    },
}))

export const StyledMainContent = styled(Box)(({ theme }) => ({}))

export const StyledSidebar = styled(Box)({})

export const StyledSectionTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: 18,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
}))

export const StyledOverview = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 15,
    lineHeight: 1.7,
    marginBottom: theme.spacing(4),
}))

export const StyledCastWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),

    [theme.breakpoints.down('md')]: {
        justifyContent: 'center',
    },
}))

// Rendered as a real router link (component={RouterLink}) so right-click works
export const StyledCastMember = styled(Box)<{ component?: React.ElementType; to?: string }>({
    textAlign: 'center',
    width: 80,
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
})

export const StyledCastImage = styled(Box)(({ theme }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: theme.palette.divider,
    marginBottom: theme.spacing(1),
}))

export const StyledCastName = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: 13,
    fontWeight: 500,
}))

export const StyledCastCharacter = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 11,
}))

export const StyledCastEpisodeCount = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: 11,
}))

export const StyledShowMoreButton = styled(Button)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: 14,
    marginBottom: theme.spacing(4),
    '&:hover': {
        backgroundColor: 'transparent',
    },
}))

// Section title + action button on one line, with clear space before the
// content below so hover states don't crowd the cards.
export const SectionHeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    '& > *': {
        marginBottom: 0,
    },
}))

export const SectionActionButton = styled(Button)({
    textTransform: 'none',
    fontWeight: 600,
})

export const EpisodeProgressBlock = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}))

export const EpisodeProgressLabel = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(0.5),
}))

export const EpisodeProgressNote = styled(Typography)(({ theme }) => ({
    display: 'block',
    color: theme.palette.text.secondary,
}))

export const EpisodeProgressBar = styled(LinearProgress)({
    height: 10,
    borderRadius: 5,
})

export const ShowAllSeasonsButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(1),
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontWeight: 600,
}))

export const DialogActionButton = styled(Button)({
    textTransform: 'none',
})

export const DialogConfirmButton = styled(Button)({
    textTransform: 'none',
    fontWeight: 600,
})

export const StyledDetailsCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: Number(theme.shape.borderRadius) * 1.5,
    padding: theme.spacing(3),
}))

export const StyledDetailItem = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    '&:last-child': {
        marginBottom: 0,
    },
}))

export const StyledDetailLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginBottom: 2,
}))

// Accepts component='div' so values containing Typography don't nest <p> in <p>
export const StyledDetailValue = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 500,
}))

// Rendered as a real router link (component={RouterLink}) so right-click works
export const StyledCollectionLink = styled(Typography)<{ component?: React.ElementType; to?: string }>(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
}))

export const StyledRecommendationsSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
}))

export const StyledRecommendationsTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: 20,
    fontWeight: 600,
    marginBottom: theme.spacing(3),
}))

export const StyledCardScroller = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2.5),
    overflowX: 'auto',
    paddingBottom: theme.spacing(2),
    '&::-webkit-scrollbar': {
        height: 6,
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.divider,
        borderRadius: 3,
    },
}))

export const SeasonsSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(6),
}))

export const SeasonsTitle = styled(Typography)(({ theme }) => ({
    fontSize: 22,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
}))

export const SeasonsGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: theme.spacing(2.5),
    marginBottom: theme.spacing(4),

    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        justifyItems: 'center',
        alignItems: 'center',
    },
}))

export const ProvidersRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    padding: `${theme.spacing(2)} 0`,
    [theme.breakpoints.down('md')]: {
        justifyContent: 'center',
    },
}))

// Informational chip only - where-to-watch entries are not links.
export const ProviderChip = styled('div')(({ theme }) => ({
    borderRadius: 999,
    padding: theme.spacing(1, 1.25),
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
}))

export const ProviderLogo = styled('img')({
    width: 22,
    height: 22,
    objectFit: 'contain',
    borderRadius: 5,
})

export const MobileButtonsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
}))

// --- Personal rating (user's own 1-10 score, separate from the TMDB score) ---

export const StyledUserRatingStars = styled(Rating)(({ theme }) => ({
    fontSize: 24,
    [theme.breakpoints.down('md')]: {
        fontSize: 20,
    },
    '& .MuiRating-iconEmpty': {
        color: theme.palette.text.secondary,
        opacity: 0.4,
    },
}))

export const StyledUserRatingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',

    [theme.breakpoints.down('md')]: {
        justifyContent: 'center',
    },
}))

export const StyledUserRatingRowSection = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up('lg')]: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    [theme.breakpoints.down('md')]: {
        margin: `${theme.spacing(1)} 0`,
    },
}))

export const StyledUserRatingLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
}))

export const StyledClearRatingButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
    padding: 2,
    '& svg': {
        fontSize: 16,
    },
    '&:hover': {
        color: theme.palette.text.primary,
    },
}))

// --- Comments section ---

export const StyledCommentsSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(4),
}))

export const StyledCommentForm = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
}))

export const StyledCommentFormActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
}))

export const StyledCommentCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: Number(theme.shape.borderRadius) * 1.5,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    textAlign: 'left',
}))

export const StyledCommentHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    flexWrap: 'wrap',
}))

export const StyledCommentAvatar = styled(Box)(({ theme }) => ({
    width: 36,
    height: 36,
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: theme.palette.divider,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    fontSize: 15,
    fontWeight: 600,
}))

export const StyledCommentAvatarImage = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
})

export const StyledCommentAuthor = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 600,
}))

// Wraps a commenter's avatar + name in a real link to their public profile
// so right-click / cmd+click work. Matches StyledCommentHeader's gap.
export const StyledCommentAuthorLink = styled(RouterLink)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    textDecoration: 'none',
    color: 'inherit',
    '&:hover .MuiTypography-root': {
        textDecoration: 'underline',
        color: theme.palette.brand.main,
    },
}))

export const StyledCommentMeta = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 12,
    // TMDB reviewer rating star, sized to sit inline with the meta text
    '& svg': {
        fontSize: 13,
        verticalAlign: 'text-top',
    },
}))

export const StyledCommentSourceChip = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 500,
}))

interface StyledCommentContentProps {
    clamped?: boolean;
}

export const StyledCommentContent = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'clamped',
})<StyledCommentContentProps>(({ theme, clamped }) => ({
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.7,
    whiteSpace: 'pre-line',
    ...(clamped && {
        display: '-webkit-box',
        WebkitLineClamp: 6,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    }),
}))

export const StyledCommentActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(0.5),
    marginLeft: 'auto',
}))

export const StyledCommentActionButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
    padding: 4,
    '& svg': {
        fontSize: 16,
    },
    '&:hover': {
        color: theme.palette.text.primary,
    },
}))

export const StyledCommentsEmpty = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 14,
    marginBottom: theme.spacing(3),
}))

export const StyledCommentsLoginHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontStyle: 'italic',
    marginRight: 'auto',
    alignSelf: 'center',
}))

export const StyledCommentEditActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
}))

export const CommentShowMoreButton = styled(Button)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: 14,
    marginTop: theme.spacing(0.5),
    marginBottom: 0,
    padding: 0,
    '&:hover': {
        backgroundColor: 'transparent',
    },
}))

export const StyledReplyForm = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    marginBottom: 0,
}))

export const StyledCommentReplies = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(2.5),
    borderLeft: `2px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}))

export const StyledReplyButton = styled(Button)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: 13,
    padding: 0,
    minWidth: 0,
    textTransform: 'none',
    alignSelf: 'flex-start',
    marginTop: theme.spacing(1),
    '&:hover': {
        backgroundColor: 'transparent',
    },
}))

export const MobileButtonRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
}))
